import { type ReactNode, type HTMLAttributes } from 'react'
import { NavLink } from 'react-router-dom'
import { cn } from '@lib/utils'
import { useAppShell } from './AppShell'

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  /** Logo/brand element */
  logo?: ReactNode
  /** Navigation items */
  children: ReactNode
  /** Footer content */
  footer?: ReactNode
}

/**
 * Sidebar navigation component
 * Works with AppShell for responsive behavior
 *
 * @example
 * <Sidebar logo={<Logo />} footer={<UserMenu />}>
 *   <SidebarNav>
 *     <SidebarNavItem icon={<HomeIcon />} label="Dashboard" href="/" />
 *   </SidebarNav>
 * </Sidebar>
 */
export function Sidebar({
  logo,
  children,
  footer,
  className,
  ...props
}: SidebarProps) {
  const { sidebarCollapsed, setSidebarCollapsed, setSidebarOpen } = useAppShell()

  return (
    <div
      className={cn('flex flex-col h-full', className)}
      {...props}
    >
      {/* Header with logo and collapse toggle */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-border shrink-0">
        {/* Logo */}
        <div
          className={cn(
            'transition-all duration-300 overflow-hidden',
            sidebarCollapsed ? 'lg:w-0 lg:opacity-0' : 'w-auto opacity-100'
          )}
        >
          {logo}
        </div>

        {/* Mobile close button */}
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="p-2 text-text-muted hover:text-text-primary lg:hidden"
          aria-label="Close sidebar"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Desktop collapse toggle */}
        <button
          type="button"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:flex p-2 text-text-muted hover:text-text-primary transition-colors"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={cn(
              'transition-transform duration-300',
              sidebarCollapsed && 'rotate-180'
            )}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>

      {/* Navigation area */}
      <nav className="flex-1 overflow-y-auto py-4">{children}</nav>

      {/* Footer */}
      {footer && (
        <div className="shrink-0 border-t border-border p-4">{footer}</div>
      )}
    </div>
  )
}

/**
 * Sidebar navigation section
 */
export interface SidebarNavProps extends HTMLAttributes<HTMLDivElement> {
  /** Section title */
  title?: string
  children: ReactNode
}

export function SidebarNav({
  title,
  children,
  className,
  ...props
}: SidebarNavProps) {
  const { sidebarCollapsed } = useAppShell()

  return (
    <div className={cn('px-2', className)} {...props}>
      {title && (
        <div
          className={cn(
            'px-3 py-2 text-caption font-medium text-text-muted uppercase tracking-wider',
            'transition-all duration-300 overflow-hidden whitespace-nowrap',
            sidebarCollapsed ? 'lg:opacity-0 lg:h-0' : 'opacity-100 h-auto'
          )}
        >
          {title}
        </div>
      )}
      <div className="space-y-1">{children}</div>
    </div>
  )
}

/**
 * Sidebar navigation item
 */
export interface SidebarNavItemProps {
  /** Icon element */
  icon: ReactNode
  /** Label text */
  label: string
  /** Link href */
  href?: string
  /** Active state */
  active?: boolean
  /** Badge content */
  badge?: ReactNode
  /** Click handler */
  onClick?: () => void
}

export function SidebarNavItem({
  icon,
  label,
  href,
  active = false,
  badge,
  onClick,
}: SidebarNavItemProps) {
  const { sidebarCollapsed, setSidebarOpen } = useAppShell()

  const handleClick = () => {
    onClick?.()
    // Close mobile sidebar on navigation
    setSidebarOpen(false)
  }

  const content = (
    <>
      <span className="shrink-0 w-5 h-5">{icon}</span>
      <span
        className={cn(
          'flex-1 truncate transition-all duration-300',
          sidebarCollapsed && 'lg:w-0 lg:opacity-0 lg:ml-0'
        )}
      >
        {label}
      </span>
      {badge && (
        <span
          className={cn(
            'shrink-0 transition-all duration-300',
            sidebarCollapsed && 'lg:hidden'
          )}
        >
          {badge}
        </span>
      )}
    </>
  )

  const className = cn(
    'flex items-center gap-3 px-3 py-2 text-body-sm font-medium',
    'transition-all duration-200',
    active
      ? 'bg-primary text-text-inverse'
      : 'text-text-secondary hover:text-text-primary hover:bg-surface-elevated',
    sidebarCollapsed && 'lg:justify-center lg:px-2'
  )

  if (href) {
    return (
      <NavLink
        to={href}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-3 py-2 text-body-sm font-medium',
            'transition-all duration-200',
            isActive
              ? 'bg-primary text-text-inverse'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-elevated',
            sidebarCollapsed && 'lg:justify-center lg:px-2'
          )
        }
        onClick={handleClick}
      >
        {content}
      </NavLink>
    )
  }

  return (
    <button type="button" className={cn(className, 'w-full')} onClick={handleClick}>
      {content}
    </button>
  )
}

/**
 * Sidebar divider
 */
export function SidebarDivider() {
  return <div className="my-2 mx-3 border-t border-border" />
}

export default Sidebar
